import { useEffect, useRef, useState, useCallback } from 'react';

export const useSocket = (url, options = {}) => {
    const { onMessage, shouldReconnect = true } = options;
    const socketRef = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState(null);
    const reconnectTimeoutRef = useRef(null);

    // Store callbacks in refs to prevent unnecessary re-connections
    const onMessageRef = useRef(onMessage);

    // Track if closure was intentional (cleanup) to avoid auto-reconnect
    const isIntentionalClose = useRef(false);

    // Update ref when onMessage changes
    useEffect(() => {
        onMessageRef.current = onMessage;
    }, [onMessage]);

    const connect = useCallback(() => {
        if (!url) return;

        // Clean up existing connection if it exists/checking
        if (socketRef.current) {
            // Mark as intentional to prevent reconnect loop
            isIntentionalClose.current = true;
            socketRef.current.close();
        }

        isIntentionalClose.current = false;

        // Simple exponential backoff or jitter could be added here, 
        // but keeping it simple for now as per original
        const socket = new WebSocket(url);

        socket.onopen = () => {
            setIsConnected(true);
            setError(null);
        };

        socket.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (onMessageRef.current) {
                    onMessageRef.current(data);
                }
            } catch (error) {
                console.error('[useSocket] Message parse error:', error);
            }
        };

        socket.onclose = (event) => {
            setIsConnected(false);

            // Only reconnect if it wasn't an intentional close (e.g. component unmount or url change)
            if (shouldReconnect && !isIntentionalClose.current) {
                // Clear any existing timeout just in case
                if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);

                reconnectTimeoutRef.current = setTimeout(() => {
                    connect();
                }, 3000);
            }
        };

        socket.onerror = (event) => {
            console.error('[useSocket] Error:', event);
            setError('Connection failed');
            setIsConnected(false);
        };

        socketRef.current = socket;

    }, [url, shouldReconnect]);

    useEffect(() => {
        connect();

        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (socketRef.current) {
                isIntentionalClose.current = true;
                socketRef.current.close();
            }
        };
    }, [connect]);

    const sendMessage = useCallback((data) => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify(data));
        }
    }, []);

    return { isConnected, error, sendMessage };
};

export default useSocket;
